import {create} from "@/store/createStore";
import type {TInstagramContent, TNonFunctionKeys} from "@/store/types";
import {produce} from "immer";
import {isIE} from "react-device-detect";
import {devtools} from "zustand/middleware";
import {immer} from "zustand/middleware/immer";

export type TCarouselMedia = TInstagramContent & {
  assetName?: string | null;
  campaign?: TCarouselCampaign; // Used for carousels with media from different campaigns (e.g. favorite content)
};

type TCarouselCampaign = {id: number; title: string};

type TCarouselVariant = "assets" | "gallery" | "social_feed";

type TState = {
  campaign?: TCarouselCampaign; // Used for carousels with media from the same campaign (e.g. gallery)
  currentIndex: number;
  currentMedia: TCarouselMedia[];
  initialMediaIndex: number;
  isIENotSupportedModalOpen: boolean;
  isOpen: boolean;
  shouldUsePersistedIndex: boolean;
  variant: TCarouselVariant;
  open({
    campaign,
    initialMediaId,
    media,
    shouldUsePersistedIndex,
    variant,
  }: {
    campaign?: TCarouselCampaign;
    initialMediaId?: number;
    media: TCarouselMedia[];
    shouldUsePersistedIndex?: boolean;
    variant?: TCarouselVariant;
  }): void;
  reset(): void;
  setCurrentIndex(index: number): void;
  setValue<K extends TNonFunctionKeys<TState>>(key: K, value: TState[K]): void;
  toggleLikedStatus(contentId: number): void;
};

const createCarousel = (storePrefix: string = "") =>
  create<TState>()(
    immer(
      devtools(
        set => ({
          currentIndex: 0,
          currentMedia: [],
          isIENotSupportedModalOpen: false,
          isOpen: false,
          shouldUsePersistedIndex: false,
          initialMediaIndex: 0,
          variant: "gallery",
          open: ({campaign, initialMediaId, media, shouldUsePersistedIndex = false, variant = "gallery"}) => {
            if (isIE) {
              return set(
                state => {
                  state.isIENotSupportedModalOpen = true;
                },
                false,
                `useCarousel${storePrefix}Store/open`,
              );
            }

            return set(
              state => {
                state.campaign = campaign;
                state.currentMedia = media;
                state.initialMediaIndex = initialMediaId ? media.findIndex(media => media.id === initialMediaId) : 0;
                state.isOpen = true;
                state.shouldUsePersistedIndex = shouldUsePersistedIndex;
                state.variant = variant;
              },
              false,
              `useCarousel${storePrefix}Store/open`,
            );
          },
          reset: () =>
            set(
              state => {
                state.isOpen = false;

                if (!storePrefix) {
                  state.currentMedia = [];
                  state.initialMediaIndex = 0;
                }
              },
              false,
              `useCarousel${storePrefix}Store/reset`,
            ),
          setCurrentIndex: index =>
            set(
              state => {
                state.currentIndex = index;
              },
              false,
              `useCarousel${storePrefix}Store/setCurrentIndex`,
            ),
          setValue: (key, value) =>
            set(
              state => {
                state[key] = value;
              },
              false,
              `useCarousel${storePrefix}Store/setValue`,
            ),
          toggleLikedStatus: (contentId: number) =>
            set(
              produce((state: TState) => {
                const mediaItem = state.currentMedia.find(media => media.instagram_media?.id === contentId);

                if (mediaItem?.instagram_media) {
                  mediaItem.instagram_media.liked = !mediaItem.instagram_media.liked;
                }
              }),
            ),
        }),
        {
          name: `useCarousel${storePrefix}Store`,
        },
      ),
    ),
  );

const useCarouselStore = createCarousel();
const useAdminCarouselStore = createCarousel("Admin");

export {useAdminCarouselStore, useCarouselStore};
